import { ApiResponse } from 'apisauce';
import { OrderProductsWithErrors } from '../../../services/modules/order-product/order-product.types';
import { getOrderProductUniqueIdentifier } from '../../../utils/getOrderProductUniqueIdentifier';
import { isSameAddProduct } from '../products/add-product/addOrderProduct.utils';
import { initOrderProducts, initSections } from '../products/helpers/orderProducts.helper';
import {
  AddOrderProductFormState,
  OrderHeadersGroupBy,
  OrderHeadersGroupByEnum,
  OrderHeadersGroupByInfo,
  OrderProduct,
  OrderProductsStateProps,
  Section,
} from '../products/orderProducts.types';
import {
  filterProductsAndRemove,
  getProductSectionIndex,
  isSameOrderProduct,
} from '../products/orderProducts.utils';
import {
  InsertProductAtIndexParams,
  OrderProductsGrouping,
  PrependProductsParams,
  RegroupSectionsParams,
  UpsertOrderProductsParams,
} from './orderProducts.state.types';

const isGroupedView = (grouping?: OrderProductsGrouping): grouping is OrderProductsGrouping =>
  !!grouping && grouping.view[0] !== OrderHeadersGroupByEnum.DEFAULT;

const regroupSections = ({ products, grouping, addProducts }: RegroupSectionsParams): Section[] => {
  const sections = initSections({
    products,
    view: grouping.view,
    viewInfo: grouping.viewInfo ?? undefined,
    orderProductsWithErrors: grouping.orderProductsWithErrors,
  });

  if (!addProducts?.length) {
    return sections;
  }

  return sections.map((section, index) => (index === 0 ? { ...section, addProducts } : section));
};

const withPrependedProducts = ({
  state,
  incoming,
  grouping,
  addProducts,
}: PrependProductsParams): OrderProductsStateProps => {
  const products = [...incoming, ...state.products];
  const base = { ...state, openBarcodeDialog: state.activeBarcodeDialog, products };

  if (isGroupedView(grouping)) {
    return {
      ...base,
      collapsedParentGroupKeys: [],
      sections: regroupSections({ products, grouping, addProducts }),
    };
  }

  return {
    ...base,
    sections: state.sections.map((section, index) =>
      index === 0
        ? {
            ...section,
            addProducts,
            products: [...incoming, ...section.products],
            productsCount: section.productsCount + incoming.length,
          }
        : section,
    ),
  };
};

const insertProductAtIndex = ({ products, product, index }: InsertProductAtIndexParams) => [
  ...products.slice(0, index),
  product,
  ...products.slice(index),
];

const upsertOrderProducts = ({
  products,
  incoming,
  productsToPrepend,
}: UpsertOrderProductsParams) => {
  const incomingByIdentifier = new Map(
    incoming.map((product) => [getOrderProductUniqueIdentifier(product), product]),
  );

  const merged = products.map(
    (product) => incomingByIdentifier.get(getOrderProductUniqueIdentifier(product)) ?? product,
  );

  const hasReplacement = merged.some((product, index) => product !== products[index]);

  if (!hasReplacement && !productsToPrepend.length) {
    return products;
  }

  return [...productsToPrepend, ...merged];
};

export const orderProductsInitialState: OrderProductsStateProps = {
  isLoading: false,
  hasFetchedProducts: false,
  error: undefined,
  sections: [] as Array<Section>,
  collapsedParentGroupKeys: [],
  products: [] as Array<OrderProduct>,
  orderItemsStatus: undefined,
  openBarcodeDialog: false,
  activeBarcodeDialog: false,
};

export const reducer = (state: OrderProductsStateProps, action: any): OrderProductsStateProps => {
  switch (action.type) {
    case 'START_FETCH_ORDER_PRODUCTS':
      return {
        ...state,
        isLoading: true,
      };
    case 'FETCH_ORDER_PRODUCTS_RESULT': {
      const { response, view, viewInfo, orderProductsWithErrors } = action.payload as {
        response: ApiResponse<OrderProduct[]>;
        view: OrderHeadersGroupBy[];
        viewInfo: OrderHeadersGroupByInfo;
        orderProductsWithErrors?: OrderProductsWithErrors;
      };
      if (!response.ok) {
        return {
          ...state,
          isLoading: false,
          hasFetchedProducts: true,
          error: 'Erro ao buscar produtos',
          sections: [],
        };
      }

      const items: OrderProduct[] = response.data || [];
      return {
        ...state,
        error: undefined,
        isLoading: false,
        hasFetchedProducts: true,
        collapsedParentGroupKeys: [],
        sections: initSections({ products: items, view, viewInfo, orderProductsWithErrors }),
        products: initOrderProducts({ products: items, view, orderProductsWithErrors, viewInfo }),
      };
    }

    case 'EXPAND_SECTION': {
      const { sectionIndex } = action.payload;
      const { parentGroupKey } = state.sections[sectionIndex];

      const newSections = [...state.sections];
      newSections[sectionIndex].isExpanded = true;
      return {
        ...state,
        collapsedParentGroupKeys: state.collapsedParentGroupKeys.filter(
          (collapsedKey) => collapsedKey !== parentGroupKey,
        ),
        sections: newSections,
      };
    }
    case 'RETRACT_SECTION': {
      const { sectionIndex } = action.payload;

      const newSections = [...state.sections];
      newSections[sectionIndex].isExpanded = false;
      return {
        ...state,
        sections: newSections,
      };
    }
    case 'EXPAND_OR_RETRACT_ALL_SECTIONS': {
      const { isExpanded } = action.payload;
      return {
        ...state,
        collapsedParentGroupKeys: [],
        sections: state.sections.map((section, index) => ({
          ...section,
          isExpanded: isExpanded || index === 0,
        })),
      };
    }
    case 'TOGGLE_PARENT_GROUP': {
      const { parentGroupKey, isExpanded } = action.payload;
      const otherCollapsedKeys = state.collapsedParentGroupKeys.filter(
        (collapsedKey) => collapsedKey !== parentGroupKey,
      );

      return {
        ...state,
        collapsedParentGroupKeys: isExpanded
          ? otherCollapsedKeys
          : [...otherCollapsedKeys, parentGroupKey],
        sections: state.sections.map((section) =>
          section.parentGroupKey === parentGroupKey ? { ...section, isExpanded } : section,
        ),
      };
    }
    case 'INSERT_NEW_ADD_PRODUCT': {
      return {
        ...state,
        sections: state.sections.map((section, index) => {
          if (index === 0) {
            return {
              ...section,
              addProducts: [...action.payload, ...(section.addProducts || [])],
            };
          }
          return section;
        }),
      };
    }
    case 'SUCCESS_AFTER_INSERT_ADD_PRODUCT': {
      const { addOrderProductState, product, grouping } = action.payload;

      return withPrependedProducts({
        state,
        incoming: [product],
        grouping,
        addProducts: state.sections[0]?.addProducts?.filter(
          (addProduct) => !isSameAddProduct(addOrderProductState, addProduct),
        ),
      });
    }
    case 'PUSH_ORDER_PRODUCTS_STATE': {
      const { orderProducts, grouping } = action.payload;

      return withPrependedProducts({
        state,
        incoming: orderProducts,
        grouping,
        addProducts: state.sections[0]?.addProducts,
      });
    }
    case 'UPSERT_ORDER_PRODUCTS_STATE': {
      const { orderProducts, grouping } = action.payload;
      const knownIdentifiers = new Set(state.products.map(getOrderProductUniqueIdentifier));
      const newProducts = orderProducts.filter(
        (product: OrderProduct) => !knownIdentifiers.has(getOrderProductUniqueIdentifier(product)),
      );

      if (newProducts.length && isGroupedView(grouping)) {
        const products = upsertOrderProducts({
          products: state.products,
          incoming: orderProducts,
          productsToPrepend: newProducts,
        });

        return {
          ...state,
          openBarcodeDialog: state.activeBarcodeDialog,
          products,
          collapsedParentGroupKeys: [],
          sections: regroupSections({
            products,
            grouping,
            addProducts: state.sections[0]?.addProducts,
          }),
        };
      }

      return {
        ...state,
        openBarcodeDialog: state.activeBarcodeDialog,
        products: upsertOrderProducts({
          products: state.products,
          incoming: orderProducts,
          productsToPrepend: newProducts,
        }),
        sections: state.sections.map((section, index) => {
          const productsToPrepend = index === 0 ? newProducts : [];
          const products = upsertOrderProducts({
            products: section.products,
            incoming: orderProducts,
            productsToPrepend,
          });

          if (products === section.products) {
            return section;
          }

          return {
            ...section,
            products,
            productsCount: section.productsCount + productsToPrepend.length,
          };
        }),
      };
    }
    case 'ERROR_AFTER_TRY_TO_ADD_PRODUCT': {
      const {
        addProduct,
        errorMessage,
      }: { addProduct: AddOrderProductFormState; errorMessage?: string } = action.payload;
      return {
        ...state,
        sections: state.sections.map((section, index) => {
          if (index === 0) {
            return {
              ...section,
              addProducts: section.addProducts?.map((addProductItem) => {
                if (isSameAddProduct(addProduct, addProductItem)) {
                  return {
                    ...addProductItem,
                    errorMessage,
                    submitButton: {
                      isSubmitting: false,
                      status: 'error',
                    },
                  };
                }
                return addProductItem;
              }),
            };
          }
          return section;
        }),
      };
    }
    case 'REMOVE_ADD_PRODUCT': {
      return {
        ...state,
        sections: state.sections.map((section, index) => {
          if (index === 0) {
            return {
              ...section,
              addProducts: section.addProducts?.filter(
                (product) => !isSameAddProduct(action.payload, product),
              ),
            };
          }
          return section;
        }),
      };
    }
    case 'REMOVE_PRODUCT': {
      const { product, sectionIndex } = action.payload;

      const productIndex = state.products.findIndex((p) => isSameOrderProduct(product, p));

      return {
        ...state,
        products: filterProductsAndRemove(state.products, product),
        sections: state.sections.map((section, index) => {
          if (index !== sectionIndex) {
            return section;
          }

          const products = filterProductsAndRemove(section.products, product);

          return {
            ...section,
            products,
            productsCount: products.length,
            lastRemovedProductIndex: productIndex,
          };
        }),
      };
    }
    case 'REMOVE_REMOVE_PRODUCT': {
      const { product, sectionIndex } = action.payload;

      return {
        ...state,
        products: insertProductAtIndex({
          products: state.products,
          product,
          index: state.sections[sectionIndex]?.lastRemovedProductIndex || 0,
        }),
        sections: state.sections.map((section, index) => {
          if (index !== sectionIndex) {
            return section;
          }

          const products = insertProductAtIndex({
            products: section.products,
            product,
            index: section.lastRemovedProductIndex || 0,
          });

          return {
            ...section,
            products,
            productsCount: products.length,
            lastRemovedProductIndex: null,
          };
        }),
      };
    }
    case 'REMOVE_PRODUCTS': {
      const { productUniqueIdentifiers } = action.payload;

      const newState = { ...state };

      newState.products = newState.products.filter(
        (product) => !productUniqueIdentifiers.includes(getOrderProductUniqueIdentifier(product)),
      );
      newState.sections = newState.sections.map((section) => {
        const sectionProductsWithoutKitProducts = section.products.filter(
          (product) => !productUniqueIdentifiers.includes(getOrderProductUniqueIdentifier(product)),
        );
        return {
          ...section,
          productsCount: sectionProductsWithoutKitProducts.length,
          products: sectionProductsWithoutKitProducts,
        };
      });

      return JSON.parse(JSON.stringify(newState));
    }

    case 'UPDATE_DELIVERY_WINDOW_AND_AVAILABILITY': {
      const { product, productIndex, sectionIndex } = action.payload;

      const newState = { ...state };

      const productSectionIndex = getProductSectionIndex({
        productIndex,
        sectionIndex,
        sections: state.sections,
      });

      const newProduct: OrderProduct = {
        ...product,
        updatingAvailability: false,
      };

      newState.sections[sectionIndex].products[productSectionIndex] = product;
      newState.products[productIndex] = newProduct;
      return JSON.parse(JSON.stringify(newState));
    }
    case 'UPDATE_COLOR_RESULT': {
      const { product, productIndex, sectionIndex } = action.payload;

      const newState = { ...state };

      const productSectionIndex = getProductSectionIndex({
        productIndex,
        sectionIndex,
        sections: state.sections,
      });

      newState.sections[sectionIndex].products[productSectionIndex] = product;
      newState.products[productIndex] = product;
      return JSON.parse(JSON.stringify(newState));
    }

    case 'LOADING_UPDATE_ORDER_PRODUCT': {
      const { productIndex, updatingAvailability, sectionIndex } = action.payload;

      const newState = { ...state };

      const newProduct: OrderProduct = {
        ...state.products[productIndex],
        updatingAvailability,
      };

      const productSectionIndex = getProductSectionIndex({
        productIndex,
        sectionIndex,
        sections: state.sections,
      });

      newState.sections[sectionIndex].products[productSectionIndex] = newProduct;
      newState.products[productIndex] = newProduct;
      return JSON.parse(JSON.stringify(newState));
    }

    case 'CLOSE_BARCODE_AFTER_READ': {
      return {
        ...state,
        openBarcodeDialog: false,
        activeBarcodeDialog: true,
      };
    }
    case 'DISABLE_BARCODE': {
      return {
        ...state,
        activeBarcodeDialog: false,
        openBarcodeDialog: false,
      };
    }
    case 'ACTIVE_BARCODE': {
      return {
        ...state,
        activeBarcodeDialog: true,
        openBarcodeDialog: true,
      };
    }
    default: {
      throw new Error('Ação do reducer não encontrada');
    }
  }
};

export const startFetchOrderProducts = () => ({ type: 'START_FETCH_ORDER_PRODUCTS' });

export const fetchOrderProductsResult = (payload: {
  response: ApiResponse<OrderProduct[]>;
  view: OrderHeadersGroupBy[];
  viewInfo: OrderHeadersGroupByInfo | null;
  orderProductsWithErrors?: OrderProductsWithErrors;
}) => ({
  type: 'FETCH_ORDER_PRODUCTS_RESULT',
  payload,
});

// section
export const insertNewAddProduct = (newAddProduct: AddOrderProductFormState[]) => ({
  type: 'INSERT_NEW_ADD_PRODUCT',
  payload: newAddProduct,
});

export const successAfterInsertAddProduct = (payload: {
  addOrderProductState: AddOrderProductFormState;
  product: OrderProduct;
  grouping: OrderProductsGrouping;
}) => ({
  type: 'SUCCESS_AFTER_INSERT_ADD_PRODUCT',
  payload,
});

export const pushOrderProductsState = (
  orderProducts: OrderProduct[],
  grouping: OrderProductsGrouping,
) => ({
  type: 'PUSH_ORDER_PRODUCTS_STATE',
  payload: { orderProducts, grouping },
});

export const upsertOrderProductsState = (
  orderProducts: OrderProduct[],
  grouping: OrderProductsGrouping,
) => ({
  type: 'UPSERT_ORDER_PRODUCTS_STATE',
  payload: { orderProducts, grouping },
});

export const errorAfterTryToAddProduct = (
  addProduct: AddOrderProductFormState,
  errorMessage?: string,
) => ({
  type: 'ERROR_AFTER_TRY_TO_ADD_PRODUCT',
  payload: { addProduct, errorMessage },
});
export const removeAddProduct = (addOrderProductState: AddOrderProductFormState) => ({
  type: 'REMOVE_ADD_PRODUCT',
  payload: addOrderProductState,
});

export const removeProduct = (product: OrderProduct, sectionIndex: number) => ({
  type: 'REMOVE_PRODUCT',
  payload: { product, sectionIndex },
});

export const removeRemoveProduct = (product: OrderProduct, sectionIndex: number) => ({
  type: 'REMOVE_REMOVE_PRODUCT',
  payload: { product, sectionIndex },
});

export const removeProducts = (productUniqueIdentifiers: string[]) => ({
  type: 'REMOVE_PRODUCTS',
  payload: { productUniqueIdentifiers },
});

export const updateDeliveryWindowAndAvailability = (payload: {
  product: OrderProduct;
  productIndex: number;
  sectionIndex: number;
}) => ({ type: 'UPDATE_DELIVERY_WINDOW_AND_AVAILABILITY', payload });

export const startUpdateOrderProduct = (productIndex: number, sectionIndex: number) => ({
  type: 'LOADING_UPDATE_ORDER_PRODUCT',
  payload: { productIndex, sectionIndex, updatingAvailability: true },
});

export const stopUpdateOrderProduct = (productIndex: number, sectionIndex: number) => ({
  type: 'LOADING_UPDATE_ORDER_PRODUCT',
  payload: { productIndex, sectionIndex, updatingAvailability: false },
});

export const updateColorResult = (payload: {
  product: OrderProduct;
  productIndex: number;
  sectionIndex: number;
}) => ({
  type: 'UPDATE_COLOR_RESULT',
  payload,
});

export const expandSection = (payload: { sectionIndex: number }) => ({
  type: 'EXPAND_SECTION',
  payload,
});

export const retractSection = (payload: { sectionIndex: number }) => ({
  type: 'RETRACT_SECTION',
  payload,
});

export const expandAllSections = () => ({
  type: 'EXPAND_OR_RETRACT_ALL_SECTIONS',
  payload: { isExpanded: true },
});

export const retractAllSections = () => ({
  type: 'EXPAND_OR_RETRACT_ALL_SECTIONS',
  payload: { isExpanded: false },
});

export const toggleParentGroup = (payload: { parentGroupKey: string; isExpanded: boolean }) => ({
  type: 'TOGGLE_PARENT_GROUP',
  payload,
});

export const activateBarcodeDialog = () => ({
  type: 'ACTIVE_BARCODE',
});

export const disableBarcodeDialog = () => ({
  type: 'DISABLE_BARCODE',
});

export const closeBarcodeDialogAfterRead = () => ({
  type: 'CLOSE_BARCODE_AFTER_READ',
});
